import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Download, Printer, ExternalLink, FileText } from "lucide-react";
import MyExternalInvoices from "../../components/MyExternalInvoices";
import WhatsAppShareButton from "../../components/WhatsAppShareButton";

export default function DistributorInvoices() {
    const [items, setItems] = useState([]);
    const [from, setFrom] = useState(""); const [to, setTo] = useState("");
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/distributors/invoices", { params: { from_date: from || undefined, to_date: to || undefined } });
            setItems(data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const totalBilled = items.reduce((s, i) => s + i.total, 0);
    const totalPaid = items.reduce((s, i) => s + (i.paid_amount || 0), 0);
    const outstanding = items.reduce((s, i) => s + (i.outstanding || 0), 0);

    return (
        <div data-testid="dist-invoices" className="space-y-4">
            <div className="flex items-center justify-between print:hidden">
                <div>
                    <div className="label-eyebrow text-sky-700">Billing</div>
                    <h1 className="font-serif text-4xl text-stone-900">My invoices</h1>
                    <p className="text-sm text-stone-600 mt-1">Download or print every invoice issued for your orders.</p>
                </div>
                <Button onClick={() => window.print()} variant="outline" className="rounded-none border-stone-400" data-testid="dist-inv-print"><Printer className="w-4 h-4 mr-2" />Print list</Button>
            </div>

            <div className="bg-white border border-stone-200 p-4 grid sm:grid-cols-3 gap-3 print:hidden">
                <div><Label className="text-xs uppercase tracking-widest text-stone-500">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none border-stone-300 mt-1" data-testid="dist-inv-from" /></div>
                <div><Label className="text-xs uppercase tracking-widest text-stone-500">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none border-stone-300 mt-1" data-testid="dist-inv-to" /></div>
                <div className="flex items-end"><Button onClick={load} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50">Apply</Button></div>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
                <KPI label="Total billed" value={formatINR(totalBilled)} />
                <KPI label="Paid" value={formatINR(totalPaid)} />
                <KPI label="Outstanding" value={formatINR(outstanding)} highlight />
            </div>

            <div className="bg-white border border-stone-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead><tr className="bg-stone-50 border-b border-stone-200">
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Invoice #</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Date</th>
                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Total</th>
                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Paid</th>
                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Outstanding</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Status</th>
                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500 print:hidden">Actions</th>
                    </tr></thead>
                    <tbody>
                        {loading ? <tr><td colSpan={7} className="p-8 text-center text-stone-500">Loading…</td></tr> :
                            items.length === 0 ? (
                                <tr><td colSpan={7} className="p-8 text-center">
                                    <FileText className="w-8 h-8 text-stone-300 mx-auto mb-2" />
                                    <p className="text-sm text-stone-500">No invoices yet.</p>
                                    <p className="text-xs text-stone-400 mt-1">Once accounts issues an invoice for your order, it will appear here.</p>
                                </td></tr>
                            ) : items.map(i => (
                                <tr key={i.id} className="border-b border-stone-100" data-testid={`dist-inv-row-${i.invoice_number}`}>
                                    <td className="p-3 font-mono">{i.invoice_number}</td>
                                    <td className="p-3 text-stone-500">{(i.created_at || "").slice(0, 10)}</td>
                                    <td className="p-3 text-right">{formatINR(i.total)}</td>
                                    <td className="p-3 text-right">{formatINR(i.paid_amount || 0)}</td>
                                    <td className={`p-3 text-right font-medium ${(i.outstanding || 0) > 0 ? "text-amber-700" : "text-sky-700"}`}>{formatINR(i.outstanding || 0)}</td>
                                    <td className="p-3"><span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${i.status === "paid" ? "border-sky-700 text-sky-700" : "border-amber-600 text-amber-700"}`}>{i.status}</span></td>
                                    <td className="p-3 text-right space-x-2 print:hidden">
                                        <Link to={`/invoice/${i.id}`} target="_blank" className="text-sky-700 inline-flex items-center text-xs underline" data-testid={`dist-inv-view-${i.invoice_number}`}>
                                            <ExternalLink className="w-3.5 h-3.5 mr-1" />View
                                        </Link>
                                        <a href={`${(api.defaults.baseURL || "")}/invoices/${i.id}/pdf`} target="_blank" rel="noreferrer" className="text-stone-700 inline-flex items-center text-xs underline" data-testid={`dist-inv-pdf-${i.invoice_number}`}>
                                            <Download className="w-3.5 h-3.5 mr-1" />Download PDF
                                        </a>
                                        <WhatsAppShareButton invoice={i} variant="ghost" label="WhatsApp" testId={`dist-inv-wa-${i.invoice_number}`} />
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>

            <MyExternalInvoices heading="Other uploaded invoices" />
        </div>
    );
}

const KPI = ({ label, value, highlight }) => (
    <div className={`bg-white border ${highlight ? "border-amber-500" : "border-stone-200"} p-4`}>
        <div className="label-eyebrow text-stone-500">{label}</div>
        <div className={`font-serif text-2xl mt-1 ${highlight ? "text-amber-700" : "text-stone-900"}`}>{value}</div>
    </div>
);
